import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { en } from './locales/en';
import { tr } from './locales/tr';

// Supported languages - easily extensible (ru, de can be added later)
export type Language = 'en' | 'tr';

// Language metadata for UI display
export const LANGUAGES: Record<Language, { code: Language; name: string; flag: string; nativeName: string }> = {
  en: { code: 'en', name: 'English', flag: '/en.png', nativeName: 'English' },
  tr: { code: 'tr', name: 'Turkish', flag: '/tr.png', nativeName: 'Türkçe' },
};

// Get all available language codes
export const AVAILABLE_LANGUAGES: Language[] = Object.keys(LANGUAGES) as Language[];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getLanguageInfo: (lang?: Language) => typeof LANGUAGES[Language];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = { en, tr };

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first, then default to 'tr' (Turkish market priority)
    const saved = localStorage.getItem('language') as Language;
    return saved && AVAILABLE_LANGUAGES.includes(saved) ? saved : 'tr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Update page title based on language
    document.title = translations[language]?.['page.title'] || 'Revomira';
  }, [language]);

  const setLanguage = (lang: Language) => {
    if (AVAILABLE_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  };

  const t = useCallback((key: string): string => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  const getLanguageInfo = (lang?: Language) => {
    const langToUse = lang || language;
    return LANGUAGES[langToUse] || LANGUAGES['tr'];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getLanguageInfo }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

